import React, { useRef, useLayoutEffect, useState } from 'react';
import gsap from 'gsap';

const INTRO_SEEN_KEY = 'avision-intro-seen';
const PARTICLE_COUNT = 10;

function shouldForceIntro() {
  try {
    return new URLSearchParams(window.location.search).has('intro');
  } catch {
    return false;
  }
}

function hasSeenIntro() {
  if (shouldForceIntro()) return false;
  try {
    return sessionStorage.getItem(INTRO_SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

function prefersReducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * Brand entrance: laser, particle burst, AVISION reveal.
 * Only opacity/transform (compositor-friendly). Once per tab session; ?intro=1 to replay.
 */
const IntroAnimation = ({ onComplete }) => {
  const containerRef = useRef(null);
  const laserRef = useRef(null);
  const letterARef = useRef(null);
  const restOfTextRef = useRef(null);
  const subTextRef = useRef(null);
  const particlesRef = useRef(null);
  const skipIntroRef = useRef(hasSeenIntro() || prefersReducedMotion());
  const [show, setShow] = useState(!skipIntroRef.current);

  useLayoutEffect(() => {
    if (skipIntroRef.current) {
      if (onComplete) onComplete();
      return undefined;
    }

    try {
      sessionStorage.setItem(INTRO_SEEN_KEY, '1');
    } catch {
      /* ignore */
    }

    const root = containerRef.current;
    if (!root) {
      if (onComplete) onComplete();
      setShow(false);
      return undefined;
    }

    const ctx = gsap.context(() => {
      const particles = particlesRef.current ? [...particlesRef.current.children] : [];

      gsap.set([letterARef.current, restOfTextRef.current, subTextRef.current], { opacity: 0 });
      gsap.set(laserRef.current, { scaleX: 0, opacity: 1, force3D: true });
      if (particles.length) {
        gsap.set(particles, { opacity: 0, x: 0, y: 0, scale: 0.4, force3D: true });
      }

      const tl = gsap.timeline({
        defaults: { force3D: true },
        onComplete: () => {
          if (onComplete) onComplete();
          gsap.to(root, {
            opacity: 0,
            duration: 0.5,
            ease: 'power2.out',
            onComplete: () => setShow(false)
          });
        }
      });

      tl.to(laserRef.current, {
        scaleX: 1,
        duration: 0.55,
        ease: 'power3.inOut'
      })
        .to(
          particles,
          {
            opacity: 0.95,
            scale: 1,
            x: (i) => Math.cos((i / PARTICLE_COUNT) * Math.PI * 2) * (90 + (i % 3) * 28),
            y: (i) => Math.sin((i / PARTICLE_COUNT) * Math.PI * 2) * (55 + (i % 4) * 18),
            duration: 0.55,
            ease: 'power3.out',
            stagger: 0.015
          },
          '-=0.2'
        )
        .to(
          particles,
          {
            opacity: 0,
            duration: 0.35,
            ease: 'power1.in'
          },
          '-=0.15'
        )
        .fromTo(
          letterARef.current,
          { opacity: 0, scale: 0.88 },
          {
            opacity: 1,
            scale: 1,
            duration: 0.4,
            ease: 'back.out(1.8)'
          },
          '-=0.35'
        )
        .to(
          restOfTextRef.current,
          {
            opacity: 1,
            duration: 0.5,
            ease: 'power2.out'
          },
          '-=0.05'
        )
        .fromTo(
          subTextRef.current,
          { y: 18, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.55, ease: 'power2.out' },
          '-=0.25'
        )
        .to(
          laserRef.current,
          {
            opacity: 0,
            scaleX: 1.5,
            duration: 0.45,
            ease: 'power2.in'
          },
          '-=0.2'
        )
        .to({}, { duration: 0.4 });
    }, containerRef);

    return () => ctx.revert();
  }, [onComplete]);

  if (!show) return null;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[9999] bg-[#0F0F23] flex items-center justify-center overflow-hidden"
      aria-hidden="true"
    >
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-[#1E1B4B] via-[#0F0F23] to-[#0F0F23]" />
      <div className="absolute inset-0 pointer-events-none opacity-40 bg-[radial-gradient(ellipse_at_center,_rgba(124,58,237,0.28)_0%,_transparent_55%)]" />

      <div className="relative z-10 text-center w-full max-w-4xl px-4 flex flex-col items-center justify-center">
        <div
          ref={particlesRef}
          className="absolute top-1/2 left-1/2 w-0 h-0 pointer-events-none"
          aria-hidden="true"
        >
          {Array.from({ length: PARTICLE_COUNT }, (_, i) => (
            <span
              key={i}
              className={`absolute block rounded-full ${
                i % 3 === 0 ? 'w-1 h-1 bg-[#EC4899]' : 'w-1.5 h-1.5 bg-[#06B6D4]'
              }`}
            />
          ))}
        </div>

        <h1 className="relative text-6xl md:text-8xl font-bold tracking-tighter text-white mb-4 flex items-center justify-center">
          <div
            ref={laserRef}
            className="absolute top-1/2 left-0 right-0 h-[3px] bg-[#06B6D4] -translate-y-1/2 z-20 origin-center intro-laser-line"
          />
          <span
            ref={letterARef}
            className="text-[#7C3AED] relative z-10 inline-block mr-1 text-7xl md:text-9xl"
          >
            A
          </span>
          <span ref={restOfTextRef} className="relative z-10 inline-block">
            VISION
          </span>
        </h1>

        <div ref={subTextRef} className="overflow-hidden">
          <p className="text-[#06B6D4] tracking-[0.35em] text-sm md:text-lg uppercase font-semibold glow-text">
            Commercial & Specialty Lighting
          </p>
        </div>
      </div>
    </div>
  );
};

export default IntroAnimation;
